/**
 * The route searches of the quiet-path gate (03 §5.2), on the 1 m grid, four-connected, one cell
 * a step (a step is a metre of crawling).
 *
 * `flood` is timeless: where can he get to through cells no static watcher ever sees?
 *
 * `searchTimed` adds the clock. Every cell carries a bit per step of the simulated window: set
 * when someone is looking at it (`watched`), and in the answer when he can be there (`reach`).
 * He may wait wherever he is for as long as nobody looks, move to a neighbour if it is unwatched
 * on the next step, and - the one liberty the model takes - in a cell nobody EVER looks at he
 * may wait for the window to come round again, so reaching it at any time reaches it at all times.
 * The whole window is searched at once, 32 steps to the machine word.
 */

const ALL = 0xffffffff;

export interface Flood {
  reached: Uint8Array;
  /** First goal cell met, or -1. */
  goal: number;
}

/** Breadth-first through `open` cells from the sources; goal cells end a branch. */
export function flood(width: number, open: Uint8Array, sources: readonly number[], goal: Uint8Array): Flood {
  const reached = new Uint8Array(open.length);
  const queue = new Int32Array(open.length);
  let head = 0;
  let tail = 0;
  let found = -1;
  for (const s of sources) {
    if (open[s] === 0 || reached[s] !== 0) continue;
    reached[s] = 1;
    queue[tail++] = s;
  }
  while (head < tail) {
    const cell = queue[head++] ?? 0;
    const x = cell % width;
    if (goal[cell] !== 0) {
      if (found < 0) found = cell;
      continue;
    }
    const around = [x > 0 ? cell - 1 : -1, x < width - 1 ? cell + 1 : -1, cell - width, cell + width];
    for (const n of around) {
      if (n < 0 || n >= open.length || open[n] === 0 || reached[n] !== 0) continue;
      reached[n] = 1;
      queue[tail++] = n;
    }
  }
  return { reached, goal: found };
}

/**
 * `open`: cells he may enter at all. `watched`: `words` words per cell, a set bit = seen on that step.
 * Sources may be occupied at any unwatched moment: he chooses where and when he comes down.
 */
export function searchTimed(
  width: number, words: number, open: Uint8Array, watched: Uint32Array, sources: readonly number[], goal: Uint8Array,
): Flood {
  const cells = open.length;
  const reach = new Uint32Array(cells * words);
  const queued = new Uint8Array(cells);
  const queue = new Int32Array(cells + 1);
  let head = 0;
  let tail = 0;
  const push = (cell: number): void => {
    if (queued[cell] !== 0) return;
    queued[cell] = 1;
    queue[tail] = cell;
    tail = (tail + 1) % queue.length;
  };
  const refuge = new Uint8Array(cells);
  for (let c = 0; c < cells; c++) {
    if (open[c] === 0) continue;
    let any = 0;
    for (let w = 0; w < words; w++) any |= watched[c * words + w] ?? 0;
    refuge[c] = any === 0 ? 1 : 0;
  }
  for (const s of sources) {
    if (open[s] === 0) continue;
    let any = 0;
    for (let w = 0; w < words; w++) any |= reach[s * words + w] = ~(watched[s * words + w] ?? 0) >>> 0;
    if (any !== 0) push(s);
  }

  let found = -1;
  const reached = new Uint8Array(cells);
  const arrive = new Uint32Array(words);
  while (head !== tail && found < 0) {
    const cell = queue[head] ?? 0;
    head = (head + 1) % queue.length;
    queued[cell] = 0;
    const x = cell % width;
    reached[cell] = 1;
    if (goal[cell] !== 0) {
      found = cell;
      break;
    }
    // Leaving on step t he arrives on step t + 1: the whole mask shifts up one bit.
    let carry = 0;
    for (let w = 0; w < words; w++) {
      const r = reach[cell * words + w] ?? 0;
      arrive[w] = ((r << 1) | carry) >>> 0;
      carry = r >>> 31;
    }
    const around = [x > 0 ? cell - 1 : -1, x < width - 1 ? cell + 1 : -1, cell - width, cell + width];
    for (const n of around) {
      if (n < 0 || n >= cells || open[n] === 0) continue;
      let changed = false;
      if (refuge[n] !== 0) {
        if (reach[n * words] === ALL) continue;
        let any = 0;
        for (let w = 0; w < words; w++) any |= arrive[w] ?? 0;
        if (any === 0) continue;
        reach.fill(ALL, n * words, (n + 1) * words);
        changed = true;
      } else {
        // Seeds: when he was already there, and when he can arrive unwatched. Then wait: each seed
        // spreads up through its run of unwatched steps - one addition per word does it (the carry
        // ripples through the run and stops at the first watched step).
        let waiting = 0;
        for (let w = 0; w < words; w++) {
          const safe = ~(watched[n * words + w] ?? 0) >>> 0;
          const was = reach[n * words + w] ?? 0;
          const seeds = (was | ((arrive[w] ?? 0) & safe)) >>> 0;
          const sum = safe + seeds + waiting;
          waiting = sum > ALL ? 1 : 0;
          const now = ((safe & ~(sum >>> 0)) | seeds) >>> 0;
          if (now !== was) {
            reach[n * words + w] = now;
            changed = true;
          }
        }
      }
      if (changed) push(n);
    }
  }
  return { reached, goal: found };
}
